import { z } from 'zod';
import { isHttpUrl } from './url.js';
import { registrationSourceSchema } from './analytics.js';

/**
 * Request/response contracts shared by the web app and the server (§53).
 *
 * The server validates every mutation with these; the client reuses them for
 * form validation so both sides agree on what is acceptable.
 */

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Введите email')
  .email('Введите корректный email')
  .max(254)
  .toLowerCase();

export const passwordSchema = z
  .string()
  .min(8, 'Пароль должен быть не короче 8 символов')
  .max(200, 'Слишком длинный пароль');

export const displayNameSchema = z
  .string()
  .trim()
  .min(2, 'Имя должно быть не короче 2 символов')
  .max(80, 'Слишком длинное имя');

export const registerSchema = z.object({
  displayName: displayNameSchema,
  email: emailSchema,
  password: passwordSchema,
  /*
    Идентификаторы согласий, которые человек отметил. Проверяются на сервере, а
    не только галочками в форме: согласие — юридический факт, и полагаться на
    то, что до сервера дойдёт только «правильный» запрос, здесь нельзя.
  */
  acceptedConsents: z.array(z.string().min(1).max(64)).max(10).default([]),
  /* Метки из ссылки, по которой человек пришёл (docs/ANALYTICS.md). Необязательны. */
  source: registrationSourceSchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  // Login does not re-apply the strength rule: an existing password is either
  // right or wrong, and echoing the rule here would leak policy changes.
  password: z.string().min(1, 'Введите пароль'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const updateProfileSchema = z.object({
  displayName: displayNameSchema,
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/**
 * Changing a password asks for the current one.
 *
 * The session cookie proves the browser is signed in, not that the person at
 * the keyboard is the owner — an unattended screen is enough to take an account
 * over. The current password is the only thing this prototype can ask for.
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Введите текущий пароль'),
    newPassword: passwordSchema,
  })
  .refine((value) => value.currentPassword !== value.newPassword, {
    path: ['newPassword'],
    message: 'Новый пароль должен отличаться от текущего',
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * Запрос ссылки восстановления. Только адрес — ответ на него одинаков всегда,
 * поэтому проверять тут нечего, кроме формы самого адреса.
 */
export const forgotPasswordSchema = z.object({
  email: emailSchema,
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/**
 * Смена пароля по ссылке.
 *
 * Токен приходит в теле запроса, а не в адресе: в ссылке он стоит после
 * решётки, и браузер такой фрагмент серверу не отправляет вовсе. Иначе ключ от
 * чужого аккаунта оседал бы в журналах веб-сервера.
 */
export const resetPasswordSchema = z.object({
  token: z.string().min(16, 'Ссылка неполная').max(256),
  password: passwordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/**
 * Подтверждение почты по ссылке из письма (§14).
 *
 * Токен так же приходит телом: в ссылке он за решёткой, и до сервера доезжает
 * только тем путём, который мы выбрали сами.
 */
export const verifyEmailSchema = z.object({
  token: z.string().min(16, 'Ссылка неполная').max(256),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

/** Правка текстов и ссылок экрана регистрации (администрирование). */
export const consentLinkSchema = z.object({
  label: z.string().trim().min(1, 'Укажите слова, которые станут ссылкой').max(120),
  /*
    Только http и https.
    
    `z.string().url()` пропускает `javascript:` и `data:` — оно проверяет, что
    адрес разбирается, а не что по нему безопасно переходить. Ссылка отсюда
    попадает в `href` на экране регистрации, который открыт всем; правило то же,
    что у ссылки в блоке обучения, и живёт оно теперь в одном месте.
  */
  url: z
    .string()
    .trim()
    .max(500)
    .refine(isHttpUrl, 'Адрес документа должен начинаться с http:// или https://'),
});

export const consentSettingSchema = z.object({
  id: z.string().trim().min(1).max(64),
  text: z.string().trim().min(1, 'Текст согласия не может быть пустым').max(600),
  links: z.array(consentLinkSchema).max(6),
  required: z.boolean(),
  revision: z.string().trim().max(120, 'Редакция — не длиннее 120 символов').default(''),
});

export const registrationFormSchema = z.object({
  heading: z.string().trim().min(1, 'Заголовок не может быть пустым').max(120),
  subheading: z.string().trim().max(240),
  /*
    Хотя бы одно согласие обязательное — на обработку персональных данных: без
    него аккаунт заводить нельзя. Снятая галочка «обязательное» у всех молча
    отменила бы требование.
  */
  consents: z
    .array(consentSettingSchema)
    .min(1)
    .max(4)
    .refine((items) => items.some((item) => item.required), {
      message: 'Хотя бы одно согласие должно быть обязательным — на обработку персональных данных',
    })
    .refine((items) => new Set(items.map((item) => item.id)).size === items.length, {
      message: 'У согласий повторяется идентификатор',
    }),
});
export type RegistrationFormInput = z.infer<typeof registrationFormSchema>;
